import { useState } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowUp, ArrowDown, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { Button } from '@/Components/ui/Button';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/Components/ui/Dialog';
import { useLayoutPreferences } from '@/hooks/useLayoutPreferences';
import { resolveTopNavOrder } from '@/hooks/layoutPreferencesStorage';
import { resolveMenuIcon, sngpcMenu, type NavItem } from '@/data/menu';
import { cn } from '@/Components/ui/utils';

function SortableTopNavTreeItem({
    item,
    index,
    total,
    isExpanded,
    onToggleExpand,
    onMoveUp,
    onMoveDown,
}: {
    item: NavItem;
    index: number;
    total: number;
    isExpanded: boolean;
    onToggleExpand: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.35 : 1,
    };

    const Icon = resolveMenuIcon(item.icon);
    const children = item.children ?? [];
    const hasChildren = children.length > 0;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                'nx-menu-order-card',
                isDragging && 'nx-menu-order-card--dragging',
            )}
        >
            <div className="nx-menu-order-row" style={{ border: 'none', borderRadius: 0 }}>
                <div className="nx-menu-order-info">
                    {hasChildren ? (
                        <button
                            type="button"
                            onClick={onToggleExpand}
                            className="nx-menu-order-expand-btn"
                            aria-label={isExpanded ? `Recolher subitens de ${item.label}` : `Expandir subitens de ${item.label}`}
                            title={isExpanded ? 'Recolher subitens' : 'Expandir subitens'}
                        >
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                    ) : (
                        <span style={{ width: '1.5rem', display: 'inline-block' }} />
                    )}
                    <span className="nx-menu-order-num">{index + 1}</span>
                    <span className="nx-menu-order-icon" aria-hidden>
                        <Icon size={16} strokeWidth={1.9} />
                    </span>
                    <div className="nx-menu-order-text">
                        <strong>{item.label}</strong>
                        {hasChildren && (
                            <span>{children.length} {children.length === 1 ? 'subitem' : 'subitens'}</span>
                        )}
                    </div>
                </div>

                <div className="nx-menu-order-actions">
                    <Button
                        type="button"
                        variant="ghost"
                        disabled={index === 0}
                        onClick={onMoveUp}
                        aria-label={`Mover ${item.label} para cima`}
                        title="Mover para cima"
                        className="nx-btn--icon-sm"
                    >
                        <ArrowUp size={14} />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        disabled={index === total - 1}
                        onClick={onMoveDown}
                        aria-label={`Mover ${item.label} para baixo`}
                        title="Mover para baixo"
                        className="nx-btn--icon-sm"
                    >
                        <ArrowDown size={14} />
                    </Button>
                    <button
                        type="button"
                        className="nx-col-picker-handle"
                        aria-label={`Arraste para reordenar ${item.label}`}
                        title="Arraste para reordenar"
                        {...attributes}
                        {...listeners}
                    >
                        <GripVertical size={16} strokeWidth={2} />
                    </button>
                </div>
            </div>

            {/* Tree Children (Subitens expandidos) */}
            {hasChildren && isExpanded && (
                <div className="nx-menu-order-tree-children">
                    {children.map((child) => (
                        <div key={child.id} className="nx-menu-order-child-item">
                            <span className="nx-menu-order-child-dot" />
                            <span>{child.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function TopNavItemOverlay({ item, index }: { item: NavItem; index: number }) {
    const Icon = resolveMenuIcon(item.icon);
    const subCount = item.children?.length ?? 0;

    return (
        <div className="nx-menu-order-row nx-menu-order-row--overlay">
            <div className="nx-menu-order-info">
                <span style={{ width: '1.5rem', display: 'inline-block' }} />
                <span className="nx-menu-order-num">{index + 1}</span>
                <span className="nx-menu-order-icon" aria-hidden>
                    <Icon size={16} strokeWidth={1.9} />
                </span>
                <div className="nx-menu-order-text">
                    <strong>{item.label}</strong>
                    {subCount > 0 && (
                        <span>{subCount} {subCount === 1 ? 'subitem' : 'subitens'}</span>
                    )}
                </div>
            </div>
            <div className="nx-menu-order-actions">
                <GripVertical size={16} strokeWidth={2} className="nx-muted" />
            </div>
        </div>
    );
}

export default function MenuOrderDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { preferences, setTopNavOrder, resetTopNavOrder } = useLayoutPreferences();
  const menu = resolveTopNavOrder(sngpcMenu, preferences.topNavOrder);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeIndex = menu.findIndex(item => item.id === activeId);
  const activeItem = menu[activeIndex];
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const saveOrder = (items: NavItem[]) => {
    const order = items.map(item => item.id);
    setTopNavOrder([...order, ...(preferences.topNavOrder ?? []).filter(id => !order.includes(id))]);
  };
  const move = (index: number, offset: number) => {
    const target = index + offset;
    if (target >= 0 && target < menu.length) saveOrder(arrayMove(menu, index, target));
  };

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="nx-menu-order-dialog">
      <DialogHeader>
        <DialogTitle>Personalizar Ordem do Menu Lateral/Superior</DialogTitle>
        <DialogDescription>Arraste os itens raiz ou use as setas para definir a sequência. Expanda os grupos para consultar seus subitens.</DialogDescription>
      </DialogHeader>
      <div className="nx-menu-order-dialog-scroll">
        <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]}
          onDragStart={event => setActiveId(String(event.active.id))}
          onDragCancel={() => setActiveId(null)}
          onDragEnd={event => {
            setActiveId(null);
            if (!event.over || event.active.id === event.over.id) return;
            const from = menu.findIndex(item => item.id === event.active.id);
            const to = menu.findIndex(item => item.id === event.over?.id);
            if (from >= 0 && to >= 0) saveOrder(arrayMove(menu, from, to));
          }}>
          <SortableContext items={menu.map(item => item.id)} strategy={verticalListSortingStrategy}>
            <div className="nx-menu-order-list">
              {menu.map((item, index) => <SortableTopNavTreeItem key={item.id} item={item} index={index} total={menu.length}
                isExpanded={Boolean(expanded[item.id])} onToggleExpand={() => setExpanded(current => ({ ...current, [item.id]: !current[item.id] }))}
                onMoveUp={() => move(index, -1)} onMoveDown={() => move(index, 1)} />)}
              {!menu.length && <p className="nx-layout-settings-note">Nenhum menu disponível para seu perfil.</p>}
            </div>
          </SortableContext>
          {createPortal(<DragOverlay dropAnimation={null} zIndex={1000}>{activeItem && <TopNavItemOverlay item={activeItem} index={activeIndex} />}</DragOverlay>, document.body)}
        </DndContext>
      </div>
      <DialogFooter>
        <Button variant="ghost" disabled={!preferences.topNavOrder?.length} onClick={resetTopNavOrder}>Restaurar ordem padrão</Button>
        <DialogClose asChild><Button>Concluir</Button></DialogClose>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
